/**
 * Custom Sub-Variant Store
 * 
 * Manages custom sub-variants (e.g. "Connectivity: Wi-Fi Only / Wi-Fi + Cellular")
 * that can be toggled per category and per device, similar to screen subconditions.
 * Sub-variants affect pricing via the price key system.
 */

const SUB_VARIANTS_KEY = "khays_custom_sub_variants";
const SUB_VARIANT_CATEGORY_KEY = "khays_sub_variant_category_settings";
const SUB_VARIANT_DEVICE_KEY = "khays_sub_variant_device_settings";
// Per-option scoping. Stores explicit OFF flags only (default = ON).
//   { [svId]: { [categoryId | deviceName]: { [option]: false } } }
const SUB_VARIANT_OPTION_CATEGORY_KEY = "khays_sub_variant_option_category_settings";
const SUB_VARIANT_OPTION_DEVICE_KEY = "khays_sub_variant_option_device_settings";

export interface CustomSubVariant {
  id: string;
  name: string; // e.g. "Connectivity"
  options: string[]; // e.g. ["Wi-Fi Only", "Wi-Fi + Cellular"]
}

// ===== Sub-Variant Definitions =====

export function loadSubVariants(): CustomSubVariant[] {
  try {
    const s = localStorage.getItem(SUB_VARIANTS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return [];
}

export function saveSubVariants(variants: CustomSubVariant[]): void {
  localStorage.setItem(SUB_VARIANTS_KEY, JSON.stringify(variants));
}

export function addSubVariant(variant: CustomSubVariant): void {
  const all = loadSubVariants();
  all.push(variant);
  saveSubVariants(all);
}

export function updateSubVariant(id: string, updates: Partial<CustomSubVariant>): void {
  const all = loadSubVariants().map(v => v.id === id ? { ...v, ...updates } : v);
  saveSubVariants(all);
}

export function removeSubVariant(id: string): void {
  saveSubVariants(loadSubVariants().filter(v => v.id !== id));
  // Also clean up category and device settings for this variant
  const catSettings = loadSubVariantCategorySettings();
  delete catSettings[id];
  saveSubVariantCategorySettings(catSettings);
  const devSettings = loadSubVariantDeviceSettings();
  delete devSettings[id];
  saveSubVariantDeviceSettings(devSettings);
}

export function addSubVariantOption(variantId: string, option: string): void {
  const all = loadSubVariants();
  const v = all.find(x => x.id === variantId);
  if (v && !v.options.includes(option)) {
    v.options.push(option);
    saveSubVariants(all);
  }
}

export function removeSubVariantOption(variantId: string, option: string): void {
  const all = loadSubVariants();
  const v = all.find(x => x.id === variantId);
  if (v) {
    v.options = v.options.filter(o => o !== option);
    saveSubVariants(all);
  }
}

// ===== Per-Category Settings =====
// Structure: { [subVariantId]: { [categoryId]: boolean } }

type CategoryMap = Record<string, Record<string, boolean>>;

function loadSubVariantCategorySettings(): CategoryMap {
  try {
    const s = localStorage.getItem(SUB_VARIANT_CATEGORY_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return {};
}

function saveSubVariantCategorySettings(settings: CategoryMap): void {
  localStorage.setItem(SUB_VARIANT_CATEGORY_KEY, JSON.stringify(settings));
}

export function isSubVariantEnabledForCategory(subVariantId: string, category: string): boolean {
  const all = loadSubVariantCategorySettings();
  return all[subVariantId]?.[category] ?? false;
}

export function setSubVariantCategoryEnabled(subVariantId: string, category: string, enabled: boolean): void {
  const all = loadSubVariantCategorySettings();
  if (!all[subVariantId]) all[subVariantId] = {};
  all[subVariantId][category] = enabled;
  saveSubVariantCategorySettings(all);
}

// ===== Per-Device Settings =====
// Structure: { [subVariantId]: { [deviceName]: boolean } }

type DeviceMap = Record<string, Record<string, boolean>>;

function loadSubVariantDeviceSettings(): DeviceMap {
  try {
    const s = localStorage.getItem(SUB_VARIANT_DEVICE_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return {};
}

function saveSubVariantDeviceSettings(settings: DeviceMap): void {
  localStorage.setItem(SUB_VARIANT_DEVICE_KEY, JSON.stringify(settings));
}

export function isSubVariantEnabledForDevice(subVariantId: string, deviceName: string): boolean {
  const all = loadSubVariantDeviceSettings();
  // Default to true if not explicitly set (inherits from category)
  return all[subVariantId]?.[deviceName] ?? true;
}

export function setSubVariantDeviceEnabled(subVariantId: string, deviceName: string, enabled: boolean): void {
  const all = loadSubVariantDeviceSettings();
  if (!all[subVariantId]) all[subVariantId] = {};
  all[subVariantId][deviceName] = enabled;
  saveSubVariantDeviceSettings(all);
}

// ===== Per-Option Scoping (per category & per device) =====
// Default = enabled. We store only explicit OFF flags.

type OptionScopeMap = Record<string, Record<string, Record<string, boolean>>>;

function loadOptionScope(key: string): OptionScopeMap {
  try {
    const s = localStorage.getItem(key);
    if (s) return JSON.parse(s);
  } catch {}
  return {};
}
function saveOptionScope(key: string, m: OptionScopeMap): void {
  localStorage.setItem(key, JSON.stringify(m));
}

export function isSubVariantOptionEnabledForCategory(svId: string, category: string, option: string): boolean {
  const m = loadOptionScope(SUB_VARIANT_OPTION_CATEGORY_KEY);
  const v = m[svId]?.[category]?.[option];
  return v !== false;
}
export function setSubVariantOptionCategoryEnabled(svId: string, category: string, option: string, enabled: boolean): void {
  const m = loadOptionScope(SUB_VARIANT_OPTION_CATEGORY_KEY);
  if (!m[svId]) m[svId] = {};
  if (!m[svId][category]) m[svId][category] = {};
  if (enabled) delete m[svId][category][option];
  else m[svId][category][option] = false;
  saveOptionScope(SUB_VARIANT_OPTION_CATEGORY_KEY, m);
}
export function isSubVariantOptionEnabledForDevice(svId: string, deviceName: string, option: string): boolean {
  const m = loadOptionScope(SUB_VARIANT_OPTION_DEVICE_KEY);
  const v = m[svId]?.[deviceName]?.[option];
  return v !== false;
}
export function setSubVariantOptionDeviceEnabled(svId: string, deviceName: string, option: string, enabled: boolean): void {
  const m = loadOptionScope(SUB_VARIANT_OPTION_DEVICE_KEY);
  if (!m[svId]) m[svId] = {};
  if (!m[svId][deviceName]) m[svId][deviceName] = {};
  if (enabled) delete m[svId][deviceName][option];
  else m[svId][deviceName][option] = false;
  saveOptionScope(SUB_VARIANT_OPTION_DEVICE_KEY, m);
}

/** Returns the effectively-enabled options for a (sv, category, device). */
export function getEnabledOptionsForDevice(sv: CustomSubVariant, category: string, deviceName: string): string[] {
  return sv.options.filter(opt =>
    isSubVariantOptionEnabledForCategory(sv.id, category, opt) &&
    isSubVariantOptionEnabledForDevice(sv.id, deviceName, opt)
  );
}

// ===== Resolution =====
// Both category-level AND device-level must be enabled for a sub-variant to show.
// Options array is filtered down to those enabled for the (category, device) pair.

export function getEnabledSubVariantsForDevice(category: string, deviceName: string): CustomSubVariant[] {
  const all = loadSubVariants();
  return all
    .filter(sv =>
      isSubVariantEnabledForCategory(sv.id, category) &&
      isSubVariantEnabledForDevice(sv.id, deviceName)
    )
    .map(sv => ({ ...sv, options: getEnabledOptionsForDevice(sv, category, deviceName) }))
    .filter(sv => sv.options.length > 0);
}
